/**
 * Cable profiles -- group 04.
 *
 * SAEC, Audience, Purist Audio Design, Stealth Audio Cables, Jorma Design,
 * Argento Audio, Skogrand Cables and Ansuz Acoustics.
 *
 * Almost every brand in this group is a boutique house that publishes
 * construction and nothing else: no resistance, no inductance, no capacitance.
 * So with no exceptions in this file the electrical fields are omitted and the
 * kit derives them from gauge, conductor material and geometry. Where a gauge
 * is not stated by the maker it is inferred from the described conductor
 * cross-section or count, and the provenance note says so.
 *
 * Power, USB and Ethernet entries describe build only -- gauge, shielding,
 * connectors, current rating, the standard met. The engine applies no signal
 * processing to those three types, and nothing here implies otherwise.
 */

import type { CableProfile } from '../../types/hardware';
import { makeCable } from '../cableKit';
import { usd } from '../kit';

export const CABLES_04: CableProfile[] = [
  // =========================================================================
  // SAEC -- Japan. Tonearm maker whose cable line is built almost entirely
  // around Furukawa PC-Triple C conductor.
  // =========================================================================
  makeCable({
    mfr: 'SAEC',
    model: 'SPC-350',
    cableType: 'speaker',
    price: usd(900, 'APPROXIMATE', 2024, 'Approximate cost of a 3 m terminated pair; SAEC sells largely by the metre in Japan.'),
    priceLengthM: 3,
    country: 'Japan',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 14,
    dielectric: 'Polyethylene',
    termination: ['spade', 'banana', 'bare'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#3f6f9f',
    tags: ['pc-triple-c', 'entry-line'],
    notes:
      'Built from PC-Triple C, a copper Furukawa forges rather than draws so that the crystal boundaries run lengthwise. It is still oxygen-free copper, so the resistivity used here is the copper figure. At 14 AWG the loop resistance lands near 0.017 ohm per metre, which over a 3 m pair puts about 0.05 ohm in series with the driver -- small, but the engine works out exactly what it does to the divider at a 4 ohm impedance minimum.',
    provenanceNote:
      'Conductor type and dielectric are as SAEC describes them. The gauge is read off the published conductor cross-section rather than stated as an AWG number, and all three electrical values are derived; SAEC publishes no resistance, inductance or capacitance.',
  }),

  makeCable({
    mfr: 'SAEC',
    model: 'SPC-700',
    cableType: 'speaker',
    price: usd(2400, 'APPROXIMATE', 2024, 'Approximate cost of a 3 m terminated pair.'),
    priceLengthM: 3,
    country: 'Japan',
    conductor: 'ofc',
    geometry: 'braided',
    gauge: 12,
    dielectric: 'Polyethylene over a cotton-filled core',
    termination: ['spade', 'banana', 'bare'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#3f6f9f',
    tags: ['pc-triple-c', 'mid-line'],
    notes:
      'Heavier PC-Triple C conductor, woven rather than simply twisted. The weave puts adjacent runs in opposition, which cuts loop inductance to roughly two thirds of a plain twisted pair and raises capacitance in the same proportion -- the standard trade, and harmless into a loudspeaker. At 12 AWG the loop resistance is about 0.010 ohm per metre.',
    provenanceNote:
      'Construction and conductor from SAEC published descriptions. Gauge inferred from the stated cross-section; electrical figures derived, as SAEC publishes none.',
  }),

  makeCable({
    mfr: 'SAEC',
    model: 'SPC-850 Stratosphere',
    cableType: 'speaker',
    price: usd(5500, 'APPROXIMATE', 2024, 'Approximate cost of a 3 m terminated pair.'),
    priceLengthM: 3,
    country: 'Japan',
    conductor: 'ofc',
    geometry: 'braided',
    gauge: 11,
    dielectric: 'Polyethylene with a damped outer jacket',
    termination: ['spade', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#2f5f8f',
    tags: ['pc-triple-c', 'flagship', 'stratosphere'],
    notes:
      'The top of the SAEC speaker range: more PC-Triple C in the same woven arrangement, taking the effective gauge to about 11 AWG and the loop resistance to roughly 0.008 ohm per metre. Over a 3 m run that is around 0.025 ohm, so the series loss into a 4 ohm minimum is a few hundredths of a decibel. The engine reports the number rather than a verdict.',
    provenanceNote:
      'Conductor and geometry from SAEC published descriptions of the Stratosphere series. Gauge inferred from the stated cross-section; all electrical values derived.',
  }),

  makeCable({
    mfr: 'SAEC',
    model: 'SL-1980',
    cableType: 'interconnect',
    price: usd(700, 'APPROXIMATE', 2024, 'Approximate cost of a 1 m RCA pair.'),
    priceLengthM: 1,
    country: 'Japan',
    conductor: 'ofc',
    geometry: 'coaxial',
    gauge: 22,
    shielding: 'Braided copper shield over a foil wrap',
    dielectric: 'Polyethylene',
    termination: ['RCA'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#3f6f9f',
    tags: ['pc-triple-c', 'single-ended'],
    notes:
      'PC-Triple C centre conductor in a conventional double-shielded coaxial build. A coaxial interconnect runs around 85 pF per metre, so a 1 m pair loads a 600 ohm tube output stage with a corner near 3 MHz and a modern 100 ohm solid-state output with one far above that. The computed in-band loss is nil to two decimal places.',
    provenanceNote:
      'Conductor, shielding and dielectric from SAEC published descriptions. Gauge inferred from the stated cross-section; capacitance derived from the coaxial geometry rather than published, as SAEC does not state it.',
  }),

  makeCable({
    mfr: 'SAEC',
    model: 'SL-2980',
    cableType: 'interconnect',
    price: usd(1300, 'APPROXIMATE', 2024, 'Approximate cost of a 1 m RCA pair.'),
    priceLengthM: 1,
    country: 'Japan',
    conductor: 'ofc',
    geometry: 'coaxial',
    gauge: 21,
    shielding: 'Double braided copper shield',
    dielectric: 'Polyethylene',
    termination: ['RCA'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#3f6f9f',
    tags: ['pc-triple-c', 'single-ended', 'mid-line'],
    notes:
      'A thicker-cored version of the same coaxial design. Line-level resistance is irrelevant at any of these gauges -- 21 AWG copper is about 0.084 ohm per metre loop, into an input impedance of tens of kilohms -- so what the model actually reads here is the shunt capacitance, and that is set by the coaxial geometry, not by the conductor size.',
    provenanceNote:
      'Construction from SAEC published descriptions. Gauge inferred; electrical values derived.',
  }),

  makeCable({
    mfr: 'SAEC',
    model: 'SL-6000 Stratosphere',
    cableType: 'interconnect',
    price: usd(3200, 'APPROXIMATE', 2024, 'Approximate cost of a 1 m pair.'),
    priceLengthM: 1,
    country: 'Japan',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 21,
    shielding: 'Foil plus braid, floated at one end on the RCA version',
    dielectric: 'Polyethylene',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#2f5f8f',
    tags: ['pc-triple-c', 'flagship', 'stratosphere'],
    notes:
      'The Stratosphere interconnect moves to a shielded twisted pair so the same cable can be built balanced. A twisted pair sits near 70 pF per metre against roughly 85 for coax, so a 1 m run is a slightly lighter capacitive load; both are far below anything that matters into a normal input.',
    provenanceNote:
      'Construction and termination options from SAEC published descriptions of the Stratosphere series. Gauge inferred; electrical values derived.',
  }),

  makeCable({
    mfr: 'SAEC',
    model: 'PL-5900',
    cableType: 'power',
    price: usd(1100, 'APPROXIMATE', 2024, 'Approximate cost of a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'Japan',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 12,
    currentRatingA: 15,
    shielding: 'Braided shield tied to the earth conductor',
    termination: ['IEC C13', 'IEC C15', 'NEMA 5-15'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#3f6f9f',
    tags: ['pc-triple-c', 'shielded'],
    notes:
      'A 12 AWG three-core mains cord using PC-Triple C for live, neutral and earth, with a braided shield bonded to earth and moulded IEC and wall connectors. Rated for 15 A. The engine applies no signal processing to power cords, so choosing this alters the chain description and the total price and leaves the audio bit-identical.',
    provenanceNote:
      'Conductor, shielding and connector options from SAEC published descriptions. Gauge inferred from the stated cross-section. No electrical model is applied to power cords, so the derived figures are recorded for completeness only.',
  }),

  makeCable({
    mfr: 'SAEC',
    model: 'SUS-380',
    cableType: 'usb',
    price: usd(600, 'APPROXIMATE', 2024, 'Approximate cost of a 1.5 m A-to-B assembly.'),
    priceLengthM: 1.5,
    country: 'Japan',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 26,
    characteristicOhms: 90,
    digitalStandard: 'USB 2.0 High Speed, 480 Mbit/s',
    shielding: 'Foil over the data pair plus an overall braid',
    termination: ['USB A to B'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#3f6f9f',
    tags: ['usb-2.0', 'separated-power-line'],
    notes:
      'A 90 ohm USB 2.0 assembly with the data pair separately foiled and the bus power line run apart from it inside the jacket. Terminations are A to B. The engine applies no signal processing to USB cables.',
    provenanceNote:
      'Construction and standard from SAEC published descriptions. Gauge is the usual figure for a USB 2.0 data pair rather than a SAEC-stated number. No electrical model is applied to USB cables.',
  }),

  // =========================================================================
  // Audience -- United States. OCC copper, deliberately small cross-sections
  // and cross-linked polyethylene throughout the range.
  // =========================================================================
  makeCable({
    mfr: 'Audience',
    model: 'Conductor e Speaker Cable',
    cableType: 'speaker',
    price: usd(700, 'APPROXIMATE', 2024, 'Approximate cost of a 2.5 m terminated pair.'),
    priceLengthM: 2.5,
    country: 'United States',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 16,
    dielectric: 'Cross-linked polyethylene',
    termination: ['spade', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#d08a4a',
    tags: ['occ', 'entry-line', 'small-gauge'],
    notes:
      'The entry Audience speaker cable, and it shows the house habit of running a small conductor: about 16 AWG, which is roughly 0.026 ohm per metre loop. Over a 2.5 m pair that is about 0.065 ohm, enough to take a 160:1 amplifier damping factor down to nearer 70:1 at the driver and to cost about 0.14 dB into a 4 ohm minimum. Whether that matters is the point of running the model.',
    provenanceNote:
      'Conductor material and dielectric from Audience published descriptions. Gauge is inferred from the physical size of the cable rather than an Audience-stated AWG figure, and the electrical values are derived; Audience publishes no resistance, inductance or capacitance.',
  }),

  makeCable({
    mfr: 'Audience',
    model: 'Au24 SE Speaker Cable',
    cableType: 'speaker',
    price: usd(3400, 'APPROXIMATE', 2024, 'Approximate cost of a 2.5 m terminated pair.'),
    priceLengthM: 2.5,
    year: 2013,
    country: 'United States',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 16,
    dielectric: 'Cross-linked polyethylene',
    termination: ['spade', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#d08a4a',
    tags: ['occ', 'au24', 'small-gauge'],
    notes:
      'Au24 in its SE revision: the same slim OCC copper geometry as the original, with revised terminations and jacketing. The cross-section is the design decision here, not an oversight -- Audience has always built Au24 thin -- and the consequence is a loop resistance around 0.026 ohm per metre that the engine puts straight into the amplifier-to-driver divider.',
    provenanceNote:
      'Conductor and dielectric from Audience published descriptions. Gauge inferred from cable size; electrical values derived.',
  }),

  makeCable({
    mfr: 'Audience',
    model: 'Au24 SX Speaker Cable',
    cableType: 'speaker',
    price: usd(5800, 'APPROXIMATE', 2024, 'Approximate cost of a 2.5 m terminated pair.'),
    priceLengthM: 2.5,
    year: 2017,
    country: 'United States',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 15,
    dielectric: 'Cross-linked polyethylene',
    termination: ['spade', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#c8763a',
    tags: ['occ', 'au24', 'upper-line'],
    notes:
      'The SX revision uses more of the same OCC copper, taking the effective gauge to around 15 AWG and the loop resistance to roughly 0.021 ohm per metre -- about a fifth less than Au24 SE over the same run. That is the whole electrical difference between them as far as the model is concerned.',
    provenanceNote:
      'Conductor and dielectric from Audience published descriptions. Gauge inferred from the increase in cross-section over Au24 SE; electrical values derived.',
  }),

  makeCable({
    mfr: 'Audience',
    model: 'Front Row Speaker Cable',
    cableType: 'speaker',
    price: usd(12500, 'APPROXIMATE', 2024, 'Approximate cost of a 2.5 m terminated pair.'),
    priceLengthM: 2.5,
    year: 2020,
    country: 'United States',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 14,
    dielectric: 'Cross-linked polyethylene',
    termination: ['spade', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#b9642c',
    tags: ['occ', 'front-row', 'flagship'],
    notes:
      'The flagship line, and the first Audience speaker cable with a conductor heavy enough to be called ordinary: about 14 AWG, near 0.017 ohm per metre loop. On a 2.5 m pair that is roughly 0.041 ohm, about a third less series resistance than Au24 SE, which is the largest electrical change anywhere in the Audience speaker range.',
    provenanceNote:
      'Conductor and dielectric from Audience published descriptions of the Front Row series. Gauge inferred from cable size; electrical values derived.',
  }),

  makeCable({
    mfr: 'Audience',
    model: 'Conductor e Interconnect',
    cableType: 'interconnect',
    price: usd(450, 'APPROXIMATE', 2024, 'Approximate cost of a 1 m RCA pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'occ-copper',
    geometry: 'coaxial',
    gauge: 22,
    shielding: 'Braided copper shield',
    dielectric: 'Cross-linked polyethylene',
    termination: ['RCA'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#d08a4a',
    tags: ['occ', 'entry-line', 'single-ended'],
    notes:
      'A single-ended OCC copper coax with a braided shield. Around 85 pF per metre, so a 1 m pair from a preamplifier with a 100 ohm output sees a low-pass corner in the tens of megahertz. The engine computes the in-band figure and it rounds to 0.00 dB, which is the correct answer for a 1 m line-level run.',
    provenanceNote:
      'Conductor, shielding and dielectric from Audience published descriptions. Gauge inferred; capacitance derived from the coaxial geometry rather than published.',
  }),

  makeCable({
    mfr: 'Audience',
    model: 'Au24 SX Interconnect',
    cableType: 'interconnect',
    price: usd(2400, 'APPROXIMATE', 2024, 'Approximate cost of a 1 m pair, RCA; XLR costs more.'),
    priceLengthM: 1,
    year: 2017,
    country: 'United States',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 22,
    shielding: 'Braided shield, grounded at the source end on the RCA build',
    dielectric: 'Cross-linked polyethylene',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#c8763a',
    tags: ['occ', 'au24', 'shield-grounded-one-end'],
    notes:
      'Twisted OCC copper pair inside a braided shield that the single-ended version grounds only at the source, so the shield carries no return current. Electrically that changes hum rejection in a real system but not the R, L and C the model reads; at roughly 70 pF per metre a 1 m run is a negligible load on any normal output stage.',
    provenanceNote:
      'Construction, shield grounding and termination options from Audience published descriptions. Gauge inferred; electrical values derived.',
  }),

  makeCable({
    mfr: 'Audience',
    model: 'Front Row Interconnect',
    cableType: 'interconnect',
    price: usd(5600, 'APPROXIMATE', 2024, 'Approximate cost of a 1 m pair.'),
    priceLengthM: 1,
    year: 2020,
    country: 'United States',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 21,
    shielding: 'Braided shield',
    dielectric: 'Cross-linked polyethylene',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#b9642c',
    tags: ['occ', 'front-row', 'flagship'],
    notes:
      'Front Row applies the heavier conductor and revised jacketing of the flagship line to a shielded twisted pair. At line level the gauge is electrically irrelevant -- 21 AWG copper is about 0.084 ohm per metre against an input impedance of tens of kilohms -- so the only quantity the model acts on is the roughly 70 pF per metre from the twisted-pair geometry.',
    provenanceNote:
      'Construction from Audience published descriptions of the Front Row series. Gauge inferred; electrical values derived.',
  }),

  makeCable({
    mfr: 'Audience',
    model: 'powerChord SE-i',
    cableType: 'power',
    price: usd(1100, 'APPROXIMATE', 2024, 'Approximate cost of a 1.75 m cord.'),
    priceLengthM: 1.75,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 12,
    currentRatingA: 15,
    termination: ['IEC C13', 'IEC C15', 'NEMA 5-15'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#d08a4a',
    tags: ['unshielded', 'entry-line'],
    notes:
      'A 12 AWG unshielded three-core cord with the live and neutral conductors twisted and a rhodium-plated connector set, rated 15 A. Nothing about a mains cord reaches the audio path in this engine, so the entry exists to record the build and the cost.',
    provenanceNote:
      'Gauge, connector plating and current rating from Audience published descriptions. No electrical model is applied to power cords; the derived figures are recorded for completeness only.',
  }),

  makeCable({
    mfr: 'Audience',
    model: 'Au24 SX powerChord',
    cableType: 'power',
    price: usd(2900, 'APPROXIMATE', 2024, 'Approximate cost of a 1.75 m cord.'),
    priceLengthM: 1.75,
    year: 2017,
    country: 'United States',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 12,
    currentRatingA: 15,
    termination: ['IEC C13', 'IEC C15', 'IEC C19', 'NEMA 5-15', 'Schuko'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#c8763a',
    tags: ['occ', 'au24'],
    notes:
      'The same 12 AWG three-core layout as the SE-i but drawn in OCC copper and offered with C19 and Schuko ends for larger amplifiers and European supplies. Rated 15 A. No signal processing is applied to power cords.',
    provenanceNote:
      'Conductor, gauge and connector options from Audience published descriptions. No electrical model is applied to power cords.',
  }),

  makeCable({
    mfr: 'Audience',
    model: 'Front Row powerChord',
    cableType: 'power',
    price: usd(5900, 'APPROXIMATE', 2024, 'Approximate cost of a 1.75 m cord.'),
    priceLengthM: 1.75,
    year: 2020,
    country: 'United States',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 11,
    currentRatingA: 20,
    termination: ['IEC C15', 'IEC C19', 'NEMA 5-15', 'NEMA 5-20', 'Schuko'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#b9642c',
    tags: ['occ', 'front-row', 'high-current'],
    notes:
      'The heaviest cord Audience builds: roughly 11 AWG OCC copper per core, a 20 A rating and C19 and NEMA 5-20 options for power amplifiers that draw large transient currents. That rating is a real, checkable safety and voltage-drop figure. It is also the whole of what this entry claims, because the engine applies no signal processing to power cords.',
    provenanceNote:
      'Gauge, current rating and connector options from Audience published descriptions of the Front Row series. No electrical model is applied to power cords.',
  }),

  makeCable({
    mfr: 'Audience',
    model: 'Au24 SX USB',
    cableType: 'usb',
    price: usd(900, 'APPROXIMATE', 2024, 'Approximate cost of a 1.5 m A-to-B assembly.'),
    priceLengthM: 1.5,
    year: 2017,
    country: 'United States',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 26,
    characteristicOhms: 90,
    digitalStandard: 'USB 2.0 High Speed, 480 Mbit/s',
    shielding: 'Foil plus braid',
    termination: ['USB A to B'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#c8763a',
    tags: ['usb-2.0', 'occ', 'au24'],
    notes:
      'A compliant 90 ohm USB 2.0 assembly built with OCC copper conductors and a foil-plus-braid shield. USB audio is packetised and error-checked and an asynchronous DAC clocks the data itself, so a compliant cable either carries the packets or fails the link. The engine applies no signal processing to USB cables.',
    provenanceNote:
      'Conductor, shielding and standard from Audience published descriptions. Gauge is the usual figure for a USB 2.0 data pair. No electrical model is applied to USB cables.',
  }),

  // =========================================================================
  // Purist Audio Design -- United States. The house signature is a damping
  // medium packed around the conductors: granular Ferox in the lower lines,
  // a fluid in the upper ones. Both are mechanical, and neither appears in
  // the electrical model.
  // =========================================================================
  makeCable({
    mfr: 'Purist Audio Design',
    model: 'Vesta Speaker Cable',
    cableType: 'speaker',
    price: usd(1400, 'APPROXIMATE', 2024, 'Approximate cost of a 2.5 m terminated pair.'),
    priceLengthM: 2.5,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'braided',
    gauge: 13,
    dielectric: 'Ferox-packed jacket over PE-insulated conductors',
    termination: ['spade', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#2f7f6f',
    tags: ['ferox', 'entry-line'],
    notes:
      'Entry Purist build: braided oxygen-free copper with the jacket packed in Ferox, a granular ferrous material the maker uses as a mechanical and magnetic damping medium. Ferox is a real, describable filler; it is not a circuit element, so it changes nothing the engine reads. What the engine does read is a 13 AWG braid, about 0.013 ohm per metre loop and a low inductance for a speaker cable.',
    provenanceNote:
      'Conductor, braid and the Ferox filling are as Purist describes them. Gauge is inferred from the cable size rather than a published AWG figure, and all electrical values are derived; Purist publishes no resistance, inductance or capacitance.',
  }),

  makeCable({
    mfr: 'Purist Audio Design',
    model: 'Corvus Speaker Cable',
    cableType: 'speaker',
    price: usd(2300, 'APPROXIMATE', 2024, 'Approximate cost of a 2.5 m terminated pair.'),
    priceLengthM: 2.5,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'braided',
    gauge: 12,
    dielectric: 'Ferox-packed jacket',
    termination: ['spade', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#2f7f6f',
    tags: ['ferox', 'lower-mid-line'],
    notes:
      'A step up in copper over Vesta at the same braided geometry: about 12 AWG, near 0.010 ohm per metre loop, so a 2.5 m pair contributes roughly 0.026 ohm. The braid keeps inductance around 0.4 uH per metre, which at 20 kHz over that run is about 0.13 ohm of reactance -- worth a hundredth or two of a decibel into a low-impedance driver.',
    provenanceNote:
      'Construction and filling from Purist published descriptions. Gauge inferred; electrical values derived.',
  }),

  makeCable({
    mfr: 'Purist Audio Design',
    model: 'Venustas Speaker Cable',
    cableType: 'speaker',
    price: usd(5500, 'APPROXIMATE', 2024, 'Approximate cost of a 2.5 m terminated pair.'),
    priceLengthM: 2.5,
    country: 'United States',
    conductor: 'copper-silver-hybrid',
    geometry: 'braided',
    gauge: 11,
    dielectric: 'Ferox-packed jacket',
    termination: ['spade', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#26705f',
    tags: ['ferox', 'mid-line'],
    notes:
      'Venustas moves to a copper and silver conductor mix at roughly 11 AWG, which is about 0.008 ohm per metre loop -- some 3 percent below the same gauge in plain copper, because silver is only about 8 percent less resistive and it is only part of the conductor. The gauge does far more here than the metallurgy, and the model shows that plainly.',
    provenanceNote:
      'Conductor mix, braid and filling from Purist published descriptions. Gauge inferred; the exact copper-to-silver proportion is not published, so the hybrid resistivity used is a mid-point rather than a Purist figure.',
  }),

  makeCable({
    mfr: 'Purist Audio Design',
    model: 'Neptune Speaker Cable',
    cableType: 'speaker',
    price: usd(9500, 'APPROXIMATE', 2024, 'Approximate cost of a 2.5 m terminated pair.'),
    priceLengthM: 2.5,
    country: 'United States',
    conductor: 'copper-silver-hybrid',
    geometry: 'braided',
    gauge: 11,
    dielectric: 'Fluid-damped jacket',
    termination: ['spade', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#1f6558',
    tags: ['fluid-damped', 'upper-line'],
    notes:
      'Neptune uses the fluid damping medium rather than granular Ferox, sealed into the jacket around the conductors. The conductor arrangement and gauge are close to Venustas, so the derived resistance and inductance are close as well; the difference between the two is a mechanical treatment the engine has no mechanism to model and does not pretend to.',
    provenanceNote:
      'Conductor and the fluid-damped construction are as Purist describes them. Gauge inferred; electrical values derived.',
  }),

  makeCable({
    mfr: 'Purist Audio Design',
    model: 'Aqueous Aureus Interconnect',
    cableType: 'interconnect',
    price: usd(1300, 'APPROXIMATE', 2024, 'Approximate cost of a 1 m RCA pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'coaxial',
    gauge: 22,
    shielding: 'Braided shield within a Ferox-packed jacket',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#2f7f6f',
    tags: ['ferox', 'lower-line'],
    notes:
      'Coaxial copper interconnect with the shield sitting inside the damped jacket. Around 85 pF per metre; a 1 m pair driven from a 600 ohm valve output rolls off above 3 MHz, which the engine reports as no measurable in-band change. That is the honest result for almost every interconnect at ordinary lengths.',
    provenanceNote:
      'Construction and filling from Purist published descriptions. Gauge inferred; capacitance derived from geometry rather than published.',
  }),

  makeCable({
    mfr: 'Purist Audio Design',
    model: 'Musaeus Interconnect',
    cableType: 'interconnect',
    price: usd(1700, 'APPROXIMATE', 2024, 'Approximate cost of a 1 m pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'coaxial',
    gauge: 22,
    shielding: 'Braided shield within a Ferox-packed jacket',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#2f7f6f',
    tags: ['ferox', 'mid-line'],
    notes:
      'Same coaxial copper core as Aqueous Aureus with a heavier damped jacket and revised connectors. Line-level series resistance at 22 AWG is about 0.11 ohm per metre loop, into an input impedance three to five orders of magnitude larger, so it is the shunt capacitance and nothing else that the model has to work with.',
    provenanceNote:
      'Construction from Purist published descriptions. Gauge inferred; electrical values derived.',
  }),

  makeCable({
    mfr: 'Purist Audio Design',
    model: 'Venustas Interconnect',
    cableType: 'interconnect',
    price: usd(2900, 'APPROXIMATE', 2024, 'Approximate cost of a 1 m pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'copper-silver-hybrid',
    geometry: 'concentric',
    gauge: 21,
    shielding: 'Concentric shield within a Ferox-packed jacket',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#26705f',
    tags: ['ferox', 'mid-line'],
    notes:
      'A concentric conductor arrangement rather than a simple coax, with copper and silver in the signal path. Concentric layers sit close together, which puts capacitance near 150 pF per metre -- more than double a twisted pair. Even so, a 1 m run into a 10 kohm input has a corner around 100 kHz at worst from a high-impedance source, and the engine states the computed figure rather than a judgement.',
    provenanceNote:
      'Conductor mix and concentric construction from Purist published descriptions. Gauge inferred; the copper-to-silver ratio is not published, so a mid-point resistivity is used.',
  }),

  makeCable({
    mfr: 'Purist Audio Design',
    model: 'Neptune Interconnect',
    cableType: 'interconnect',
    price: usd(5200, 'APPROXIMATE', 2024, 'Approximate cost of a 1 m pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'copper-silver-hybrid',
    geometry: 'concentric',
    gauge: 21,
    shielding: 'Concentric shield within a fluid-damped jacket',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#1f6558',
    tags: ['fluid-damped', 'upper-line'],
    notes:
      'The fluid-damped version of the concentric Purist interconnect. Construction, gauge and geometry match Venustas closely enough that the derived electrical figures are near-identical; the price difference buys the damping medium and the finish, and the engine will report the two as behaving the same because on the three quantities it models they are.',
    provenanceNote:
      'Construction from Purist published descriptions. Gauge inferred; electrical values derived and, by construction, close to the Venustas interconnect.',
  }),

  makeCable({
    mfr: 'Purist Audio Design',
    model: '25th Anniversary Interconnect',
    cableType: 'interconnect',
    price: usd(9500, 'APPROXIMATE', 2024, 'Approximate cost of a 1 m pair.'),
    priceLengthM: 1,
    year: 2011,
    country: 'United States',
    conductor: 'copper-silver-hybrid',
    geometry: 'concentric',
    gauge: 20,
    shielding: 'Multiple concentric shields within a fluid-damped jacket',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#17594d',
    tags: ['fluid-damped', 'flagship', 'limited-run'],
    notes:
      'The anniversary flagship: a heavier concentric conductor set, multiple shield layers and the fluid damping medium. The shield count is a real construction fact that bears on rejection of external fields in a real room; it is not one of the three quantities in the electrical model, so the engine leaves it in the description and works from the derived R, L and C.',
    provenanceNote:
      'Construction and materials from Purist published descriptions of the 25th Anniversary series. Gauge inferred; electrical values derived.',
  }),

  makeCable({
    mfr: 'Purist Audio Design',
    model: 'Venustas AC Power Cord',
    cableType: 'power',
    price: usd(2600, 'APPROXIMATE', 2024, 'Approximate cost of a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 11,
    currentRatingA: 15,
    shielding: 'Shielded, within a Ferox-packed jacket',
    termination: ['IEC C13', 'IEC C15', 'IEC C19', 'NEMA 5-15', 'Schuko'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#26705f',
    tags: ['ferox', 'shielded'],
    notes:
      'A heavy three-core mains cord, roughly 11 AWG per conductor, shielded and packed with Ferox, offered with C13, C15, C19 and both American and European wall ends and rated 15 A. That gauge and rating are the substance of the entry. The engine applies no signal processing to power cords.',
    provenanceNote:
      'Gauge, shielding, filling and connector options from Purist published descriptions. No electrical model is applied to power cords; the derived figures are recorded for completeness only.',
  }),

  makeCable({
    mfr: 'Purist Audio Design',
    model: 'Neptune AC Power Cord',
    cableType: 'power',
    price: usd(4500, 'APPROXIMATE', 2024, 'Approximate cost of a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'United States',
    conductor: 'copper-silver-hybrid',
    geometry: 'twisted-pair',
    gauge: 10,
    currentRatingA: 20,
    shielding: 'Shielded, within a fluid-damped jacket',
    termination: ['IEC C15', 'IEC C19', 'NEMA 5-15', 'NEMA 5-20', 'Schuko'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#1f6558',
    tags: ['fluid-damped', 'shielded', 'high-current'],
    notes:
      'About 10 AWG per core with copper and silver in the conductor, shielded, fluid-damped and rated 20 A with C19 and NEMA 5-20 options. A cord this heavy has a genuinely low voltage drop under a large current draw, which is a mains-side engineering fact and not an audio-path claim. No signal processing is applied to power cords.',
    provenanceNote:
      'Gauge, current rating, shielding and connector options from Purist published descriptions. No electrical model is applied to power cords.',
  }),

  makeCable({
    mfr: 'Purist Audio Design',
    model: 'Vesta USB',
    cableType: 'usb',
    price: usd(500, 'APPROXIMATE', 2024, 'Approximate cost of a 1.5 m A-to-B assembly.'),
    priceLengthM: 1.5,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 26,
    characteristicOhms: 90,
    digitalStandard: 'USB 2.0 High Speed, 480 Mbit/s',
    shielding: 'Foil over the data pair plus an overall braid',
    termination: ['USB A to B'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#2f7f6f',
    tags: ['usb-2.0', 'entry-line'],
    notes:
      'A 90 ohm USB 2.0 assembly with a foiled data pair inside an overall braid, terminated A to B. The engine applies no signal processing to USB cables.',
    provenanceNote:
      'Construction and standard from Purist published descriptions. Gauge is the usual figure for a USB 2.0 data pair. No electrical model is applied to USB cables.',
  }),

  // =========================================================================
  // Stealth Audio Cables -- United States. Serguei Timachev builds the top
  // interconnects around amorphous-alloy conductors suspended in air-filled
  // tubes, which is a genuinely unusual construction with a measurable
  // consequence: very low capacitance per metre.
  // =========================================================================
  makeCable({
    mfr: 'Stealth Audio Cables',
    model: 'Sakra',
    cableType: 'interconnect',
    price: usd(14000, 'APPROXIMATE', 2024, 'Approximate cost of a 1 m pair.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'silver-gold-alloy',
    geometry: 'air-tube',
    gauge: 24,
    shielding: 'Braided shield outside the air tubes',
    dielectric: 'Air within PTFE tubes',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#6c7480',
    tags: ['air-dielectric', 'amorphous-alloy', 'flagship'],
    notes:
      'The flagship Stealth interconnect: fine conductors held away from each other inside air-filled PTFE tubes. Air is the lowest-permittivity dielectric available short of vacuum, and the geometry drives capacitance down to roughly 28 pF per metre, well under half a normal twisted pair. Against a 600 ohm valve output a 1 m run corners near 9 MHz. The engine computes it and reports what is left in band, which is nothing.',
    provenanceNote:
      'Air-tube construction and the amorphous alloy conductor are as Stealth describes them. This schema has no entry for an amorphous metal, so the silver-gold alloy resistivity is used as the closest available value and the derived resistance should be read as an approximation. Gauge inferred; Stealth publishes no electrical figures.',
  }),

  makeCable({
    mfr: 'Stealth Audio Cables',
    model: 'Indra',
    cableType: 'interconnect',
    price: usd(7500, 'APPROXIMATE', 2024, 'Approximate cost of a 1 m pair.'),
    priceLengthM: 1,
    year: 2003,
    country: 'United States',
    conductor: 'silver-gold-alloy',
    geometry: 'air-tube',
    gauge: 24,
    shielding: 'Braided shield outside the air tubes',
    dielectric: 'Air within PTFE tubes',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#6c7480',
    tags: ['air-dielectric', 'amorphous-alloy'],
    notes:
      'The cable that established the Stealth air-tube approach, and still the same principle as Sakra with fewer conductors. The point of the construction is that the dielectric is mostly air, so the shunt capacitance the model reads is roughly a third of a coaxial cable of the same length.',
    provenanceNote:
      'Construction from Stealth published descriptions. The amorphous alloy conductor is mapped to the nearest resistivity in this schema, so the derived resistance is approximate; gauge inferred; no published electrical figures exist.',
  }),

  makeCable({
    mfr: 'Stealth Audio Cables',
    model: 'Hyperphono',
    cableType: 'interconnect',
    price: usd(5000, 'APPROXIMATE', 2024, 'Approximate cost of a 1.2 m tonearm lead.'),
    priceLengthM: 1.2,
    country: 'United States',
    conductor: 'pure-silver',
    geometry: 'twisted-pair',
    gauge: 26,
    shielding: 'Full-coverage shield with a separate ground wire',
    dielectric: 'PTFE',
    termination: ['RCA', 'XLR', 'DIN'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#6c7480',
    tags: ['phono', 'shielded', 'separate-ground'],
    notes:
      'A tonearm lead, so shielding matters far more here than anywhere else in this file: a moving-coil cartridge puts out a few hundred microvolts and the phono stage adds 60 dB or more of gain, which amplifies any picked-up hum along with the signal. Stealth uses a full-coverage shield and a separate ground wire to the phono stage. Capacitance still matters for a moving-magnet cartridge, where the cable and the load form a resonance in the audio band; at roughly 70 pF per metre a 1.2 m lead adds about 84 pF to whatever the phono input already presents.',
    provenanceNote:
      'Construction, shielding and termination options from Stealth published descriptions. Gauge inferred; capacitance derived from the twisted-pair geometry rather than published.',
  }),

  makeCable({
    mfr: 'Stealth Audio Cables',
    model: 'Varidig Sextet',
    cableType: 'interconnect',
    price: usd(3500, 'APPROXIMATE', 2024, 'Approximate cost of a single 1 m digital lead.'),
    priceLengthM: 1,
    country: 'United States',
    conductor: 'pure-silver',
    geometry: 'coaxial',
    gauge: 24,
    characteristicOhms: 75,
    shielding: 'Multiple braided shields',
    dielectric: 'PTFE',
    digitalStandard: 'S/PDIF coaxial, 75 ohm',
    termination: ['RCA', 'BNC'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#5c6470',
    tags: ['spdif', '75-ohm', 'digital'],
    notes:
      'A 75 ohm S/PDIF lead built to a controlled impedance with several shield layers, offered on RCA or BNC. Impedance match matters on a digital coax because reflections from a mismatched termination land back in the eye pattern; that is a transmission-line fact, not a tonal one. The engine treats this as an interconnect and models its R, L and C, which on a digital link is not the mechanism anyway.',
    provenanceNote:
      'Construction, impedance and termination options from Stealth published descriptions. The 75 ohm figure is the design impedance for the standard; gauge inferred and per-metre R, L and C derived.',
  }),

  makeCable({
    mfr: 'Stealth Audio Cables',
    model: 'Nanofiber',
    cableType: 'speaker',
    price: usd(13000, 'APPROXIMATE', 2024, 'Approximate cost of a 2.5 m terminated pair.'),
    priceLengthM: 2.5,
    country: 'United States',
    conductor: 'silver-plated-copper',
    geometry: 'litz',
    gauge: 12,
    dielectric: 'PTFE over individually insulated strands',
    termination: ['spade', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#5c6470',
    tags: ['litz', 'fine-strand', 'flagship'],
    notes:
      'Very large numbers of extremely fine, individually insulated strands -- a litz construction. Individually insulating the strands means the current cannot crowd into the outermost ones at high frequency, which is a real effect but a small one at audio: the skin depth in copper at 20 kHz is about 0.47 mm, larger than most strands in an ordinary cable anyway. What the model reads is a 12 AWG equivalent, around 0.010 ohm per metre loop, and an inductance near 0.35 uH per metre.',
    provenanceNote:
      'Litz construction and strand insulation are as Stealth describes them. Equivalent gauge is inferred from the total conductor cross-section; electrical values derived.',
  }),

  makeCable({
    mfr: 'Stealth Audio Cables',
    model: 'Hybrid MLT',
    cableType: 'speaker',
    price: usd(4500, 'APPROXIMATE', 2024, 'Approximate cost of a 2.5 m terminated pair.'),
    priceLengthM: 2.5,
    country: 'United States',
    conductor: 'copper-silver-hybrid',
    geometry: 'braided',
    gauge: 12,
    dielectric: 'PTFE',
    termination: ['spade', 'banana'],
    confidence: 'ESTIMATED',
    accent: '#6c7480',
    tags: ['hybrid-conductor', 'mid-line'],
    notes:
      'A braided speaker cable mixing copper and silver conductors. At about 12 AWG the loop resistance is near 0.010 ohm per metre and the braid holds inductance around 0.4 uH per metre; over a 2.5 m pair that is roughly 0.026 ohm of resistance and 0.13 ohm of reactance at 20 kHz, which the engine puts into the divider with the loudspeaker impedance curve.',
    provenanceNote:
      'Stealth publishes limited detail on this model. Conductor mix and braided geometry are read from dealer descriptions, gauge is inferred from cable size, and all electrical values are derived, so this entry is marked estimated rather than manufacturer spec.',
  }),

  makeCable({
    mfr: 'Stealth Audio Cables',
    model: 'Dream V16',
    cableType: 'power',
    price: usd(9000, 'APPROXIMATE', 2024, 'Approximate cost of a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 10,
    currentRatingA: 20,
    shielding: 'Shielded',
    termination: ['IEC C15', 'IEC C19', 'NEMA 5-15', 'NEMA 5-20', 'Schuko'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#5c6470',
    tags: ['high-current', 'shielded', 'flagship'],
    notes:
      'The heaviest cord in the Dream line, around 10 AWG per core, shielded, rated 20 A and available with C19 and NEMA 5-20 ends for large power amplifiers. The gauge and rating are the real content of this entry. The engine applies no signal processing to power cords, so this changes the chain description and the price and nothing else.',
    provenanceNote:
      'Gauge, current rating and connector options from Stealth published descriptions. No electrical model is applied to power cords; the derived figures are recorded for completeness only.',
  }),

  makeCable({
    mfr: 'Stealth Audio Cables',
    model: 'Dream V12',
    cableType: 'power',
    price: usd(5000, 'APPROXIMATE', 2024, 'Approximate cost of a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 11,
    currentRatingA: 15,
    shielding: 'Shielded',
    termination: ['IEC C13', 'IEC C15', 'NEMA 5-15', 'Schuko'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#6c7480',
    tags: ['shielded'],
    notes:
      'The mid Dream cord: about 11 AWG per core, shielded, 15 A rated, with the usual C13, C15 and Schuko options. No signal processing is applied to power cords.',
    provenanceNote:
      'Gauge, current rating and connector options from Stealth published descriptions. No electrical model is applied to power cords.',
  }),

  makeCable({
    mfr: 'Stealth Audio Cables',
    model: 'Cloude Nine',
    cableType: 'power',
    price: usd(1800, 'APPROXIMATE', 2024, 'Approximate cost of a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'United States',
    conductor: 'ofc',
    geometry: 'twisted-pair',
    gauge: 12,
    currentRatingA: 15,
    termination: ['IEC C13', 'NEMA 5-15', 'Schuko'],
    confidence: 'ESTIMATED',
    accent: '#6c7480',
    tags: ['entry-line'],
    notes:
      'The entry Stealth mains cord: a 12 AWG three-core build with moulded IEC and wall ends, rated 15 A. No signal processing is applied to power cords.',
    provenanceNote:
      'Stealth publishes little detail on this cord beyond the connector options. Gauge and current rating are inferred from the physical size and the standard rating for that gauge, so the entry is marked estimated. No electrical model is applied to power cords.',
  }),

  makeCable({
    mfr: 'Stealth Audio Cables',
    model: 'USB-T',
    cableType: 'usb',
    price: usd(1200, 'APPROXIMATE', 2024, 'Approximate cost of a 1.5 m A-to-B assembly.'),
    priceLengthM: 1.5,
    country: 'United States',
    conductor: 'pure-silver',
    geometry: 'twisted-pair',
    gauge: 26,
    characteristicOhms: 90,
    digitalStandard: 'USB 2.0 High Speed, 480 Mbit/s',
    shielding: 'Foil over the data pair plus an overall braid',
    termination: ['USB A to B'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#5c6470',
    tags: ['usb-2.0', 'silver-conductor', 'split-power'],
    notes:
      'A 90 ohm USB 2.0 assembly using silver data conductors, with the bus power line run separately from the twisted data pair inside the jacket. Terminated A to B. The engine applies no signal processing to USB cables.',
    provenanceNote:
      'Conductor, shielding and the separated power line are as Stealth describes them. Gauge is the usual figure for a USB 2.0 data pair. No electrical model is applied to USB cables.',
  }),

  // =========================================================================
  // Jorma Design -- Sweden. Solid-core conductors loosely housed in PTFE
  // tubes so the bulk of the dielectric is air. Same principle top to bottom;
  // the tiers differ in conductor count and section.
  // =========================================================================
  makeCable({
    mfr: 'Jorma Design',
    model: 'Origo Speaker Cable',
    cableType: 'speaker',
    price: usd(3000, 'APPROXIMATE', 2024, 'Approximate cost of a 2.5 m terminated pair.'),
    priceLengthM: 2.5,
    country: 'Sweden',
    conductor: 'occ-copper',
    geometry: 'air-tube',
    gauge: 14,
    dielectric: 'Air within PTFE tubes',
    termination: ['spade', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#3b6ea5',
    tags: ['air-dielectric', 'solid-conductor', 'entry-line'],
    notes:
      'Solid conductors run loose inside PTFE tubes so the space between them is mostly air. For a speaker cable that geometry trades the other way from a braid: capacitance falls to roughly 28 pF per metre, which is irrelevant into a loudspeaker, while inductance rises to around 0.9 uH per metre. Over a 2.5 m pair that is about 2.2 uH, or 0.28 ohm of reactance at 20 kHz -- around 0.02 dB into a 4 ohm load, and more on a longer run.',
    provenanceNote:
      'Air-tube construction and solid conductors are as Jorma describes them. Gauge is inferred from the conductor count and section; Jorma publishes no resistance, inductance or capacitance, so all three are derived.',
  }),

  makeCable({
    mfr: 'Jorma Design',
    model: 'Origo Interconnect',
    cableType: 'interconnect',
    price: usd(1800, 'APPROXIMATE', 2024, 'Approximate cost of a 1 m pair.'),
    priceLengthM: 1,
    country: 'Sweden',
    conductor: 'occ-copper',
    geometry: 'air-tube',
    gauge: 22,
    shielding: 'Braided shield outside the tubes',
    dielectric: 'Air within PTFE tubes',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#3b6ea5',
    tags: ['air-dielectric', 'solid-conductor', 'entry-line'],
    notes:
      'The same air-spaced construction at interconnect scale, which is where it pays: capacitance near 28 pF per metre is about 40 percent of a normal twisted pair, so a 1 m run loads even a high-impedance valve output very lightly. The engine computes the resulting corner and reports the in-band effect, which at these values is nil.',
    provenanceNote:
      'Construction from Jorma published descriptions. Gauge inferred from conductor section; electrical values derived.',
  }),

  makeCable({
    mfr: 'Jorma Design',
    model: 'Prime Speaker Cable',
    cableType: 'speaker',
    price: usd(6500, 'APPROXIMATE', 2024, 'Approximate cost of a 2.5 m terminated pair.'),
    priceLengthM: 2.5,
    country: 'Sweden',
    conductor: 'occ-copper',
    geometry: 'air-tube',
    gauge: 13,
    dielectric: 'Air within PTFE tubes',
    termination: ['spade', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#3b6ea5',
    tags: ['air-dielectric', 'solid-conductor', 'mid-line'],
    notes:
      'More conductors in the same tube arrangement, taking the effective gauge to about 13 AWG and the loop resistance to roughly 0.013 ohm per metre. Adding conductors in parallel also lowers inductance slightly, but the air spacing keeps it high by speaker-cable standards, which is the honest cost of the low-capacitance geometry.',
    provenanceNote:
      'Construction from Jorma published descriptions. Gauge inferred from conductor count and section; electrical values derived.',
  }),

  makeCable({
    mfr: 'Jorma Design',
    model: 'Prime Interconnect',
    cableType: 'interconnect',
    price: usd(3600, 'APPROXIMATE', 2024, 'Approximate cost of a 1 m pair.'),
    priceLengthM: 1,
    country: 'Sweden',
    conductor: 'occ-copper',
    geometry: 'air-tube',
    gauge: 22,
    shielding: 'Braided shield outside the tubes',
    dielectric: 'Air within PTFE tubes',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#3b6ea5',
    tags: ['air-dielectric', 'solid-conductor', 'mid-line'],
    notes:
      'Prime keeps the Origo geometry and changes the conductor preparation and the shield. At line level neither of those alters the quantities the model reads, and the entry says so rather than filling the gap with adjectives: this is an air-spaced shielded pair at roughly 28 pF per metre.',
    provenanceNote:
      'Construction from Jorma published descriptions. Gauge inferred; electrical values derived and, by construction, very close to the Origo interconnect.',
  }),

  makeCable({
    mfr: 'Jorma Design',
    model: 'Duality Speaker Cable',
    cableType: 'speaker',
    price: usd(9500, 'APPROXIMATE', 2024, 'Approximate cost of a 2.5 m terminated pair.'),
    priceLengthM: 2.5,
    country: 'Sweden',
    conductor: 'occ-copper',
    geometry: 'air-tube',
    gauge: 13,
    dielectric: 'Air within PTFE tubes',
    termination: ['spade', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#33628f',
    tags: ['air-dielectric', 'solid-conductor', 'upper-line'],
    notes:
      'Duality uses two conductor sizes in parallel within the same air-tube housing. Paralleling different sections lowers the combined resistance the way any parallel path does; the arithmetic is ordinary, and the resulting equivalent gauge is what the model uses.',
    provenanceNote:
      'The mixed-section conductor arrangement is as Jorma describes it. The equivalent gauge is inferred from the combined cross-section rather than published, and all electrical values are derived.',
  }),

  makeCable({
    mfr: 'Jorma Design',
    model: 'Duality Interconnect',
    cableType: 'interconnect',
    price: usd(5500, 'APPROXIMATE', 2024, 'Approximate cost of a 1 m pair.'),
    priceLengthM: 1,
    country: 'Sweden',
    conductor: 'occ-copper',
    geometry: 'air-tube',
    gauge: 22,
    shielding: 'Braided shield outside the tubes',
    dielectric: 'Air within PTFE tubes',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#33628f',
    tags: ['air-dielectric', 'solid-conductor', 'upper-line'],
    notes:
      'The interconnect version of the mixed-section idea, in the usual Jorma air tubes with an outer braided shield. Capacitance stays near 28 pF per metre because it is set by the spacing and the air, not by what is inside the tube.',
    provenanceNote:
      'Construction from Jorma published descriptions. Equivalent gauge inferred; electrical values derived.',
  }),

  makeCable({
    mfr: 'Jorma Design',
    model: 'Trinity Speaker Cable',
    cableType: 'speaker',
    price: usd(16000, 'APPROXIMATE', 2024, 'Approximate cost of a 2.5 m terminated pair.'),
    priceLengthM: 2.5,
    country: 'Sweden',
    conductor: 'occ-copper',
    geometry: 'air-tube',
    gauge: 13,
    dielectric: 'Air within PTFE tubes',
    termination: ['spade', 'banana'],
    confidence: 'ESTIMATED',
    accent: '#2b5680',
    tags: ['air-dielectric', 'solid-conductor', 'upper-line'],
    notes:
      'Three conductor groups in the air-tube housing, with heavier tubing and a damped outer jacket. The jacket work is mechanical and does not enter the electrical model; the conductor total does, and it lands near 13 AWG equivalent.',
    provenanceNote:
      'Jorma states the construction but not the conductor alloy or section for this model. Copper is assumed on the basis of the rest of the range, the gauge is inferred from cable size, and the entry is marked estimated for that reason.',
  }),

  makeCable({
    mfr: 'Jorma Design',
    model: 'Trinity Interconnect',
    cableType: 'interconnect',
    price: usd(9000, 'APPROXIMATE', 2024, 'Approximate cost of a 1 m pair.'),
    priceLengthM: 1,
    country: 'Sweden',
    conductor: 'occ-copper',
    geometry: 'air-tube',
    gauge: 22,
    shielding: 'Braided shield outside the tubes',
    dielectric: 'Air within PTFE tubes',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#2b5680',
    tags: ['air-dielectric', 'solid-conductor', 'upper-line'],
    notes:
      'Air-spaced shielded pair with the heavier tubing and jacket of the Trinity line. Everything the engine reads here comes from the geometry, and the geometry is the same one Jorma has used since Origo.',
    provenanceNote:
      'Conductor alloy and section are not published for this model; copper is assumed from the rest of the range and the gauge is inferred, so the entry is marked estimated.',
  }),

  makeCable({
    mfr: 'Jorma Design',
    model: 'Statement Speaker Cable',
    cableType: 'speaker',
    price: usd(30000, 'APPROXIMATE', 2024, 'Approximate cost of a 2.5 m terminated pair.'),
    priceLengthM: 2.5,
    country: 'Sweden',
    conductor: 'copper-silver-hybrid',
    geometry: 'air-tube',
    gauge: 12,
    dielectric: 'Air within PTFE tubes',
    termination: ['spade', 'banana'],
    confidence: 'ESTIMATED',
    accent: '#234a70',
    tags: ['air-dielectric', 'solid-conductor', 'flagship'],
    notes:
      'The largest cable Jorma builds, with the most conductor in the air-tube housing: about 12 AWG equivalent, near 0.010 ohm per metre loop. Its inductance is still around 0.9 uH per metre, so on a long run into a low-impedance loudspeaker the high-frequency series reactance is the term that shows up in the model, not the resistance.',
    provenanceNote:
      'Jorma describes a mixed copper and silver conductor for this model without stating proportions or section. A mid-point hybrid resistivity and an inferred gauge are used, so the entry is marked estimated.',
  }),

  makeCable({
    mfr: 'Jorma Design',
    model: 'Statement Interconnect',
    cableType: 'interconnect',
    price: usd(16000, 'APPROXIMATE', 2024, 'Approximate cost of a 1 m pair.'),
    priceLengthM: 1,
    country: 'Sweden',
    conductor: 'copper-silver-hybrid',
    geometry: 'air-tube',
    gauge: 21,
    shielding: 'Braided shield outside the tubes',
    dielectric: 'Air within PTFE tubes',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'ESTIMATED',
    accent: '#234a70',
    tags: ['air-dielectric', 'solid-conductor', 'flagship'],
    notes:
      'The flagship interconnect: heavier conductors in the same air tubes with a braided shield. At line level a heavier conductor changes nothing measurable, and the entry does not claim it does; the capacitance from the air spacing is the one quantity here with a computable consequence.',
    provenanceNote:
      'Conductor proportions and section are not published. A mid-point hybrid resistivity and an inferred gauge are used, so the entry is marked estimated.',
  }),

  makeCable({
    mfr: 'Jorma Design',
    model: 'AC Landa',
    cableType: 'power',
    price: usd(3200, 'APPROXIMATE', 2024, 'Approximate cost of a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'Sweden',
    conductor: 'occ-copper',
    geometry: 'twisted-pair',
    gauge: 12,
    currentRatingA: 16,
    shielding: 'Shielded',
    termination: ['IEC C13', 'IEC C15', 'Schuko', 'NEMA 5-15'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#3b6ea5',
    tags: ['shielded'],
    notes:
      'The Jorma mains cord: a shielded 12 AWG three-core build rated 16 A, with Schuko and IEC options for European supplies and a NEMA end for American ones. The engine applies no signal processing to power cords, so this entry is a record of the build and the cost.',
    provenanceNote:
      'Gauge, shielding, rating and connector options from Jorma published descriptions. No electrical model is applied to power cords; the derived figures are recorded for completeness only.',
  }),

  // =========================================================================
  // Argento Audio -- Denmark. A silver house: high-purity solid silver
  // conductors in air-spaced PTFE across the whole range.
  // =========================================================================
  makeCable({
    mfr: 'Argento Audio',
    model: 'Organic Interconnect',
    cableType: 'interconnect',
    price: usd(2200, 'APPROXIMATE', 2024, 'Approximate cost of a 1 m pair.'),
    priceLengthM: 1,
    country: 'Denmark',
    conductor: 'pure-silver',
    geometry: 'air-tube',
    gauge: 24,
    shielding: 'Braided shield, floated at the load end on the RCA build',
    dielectric: 'Air within PTFE tubes',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#aab4c0',
    tags: ['silver-conductor', 'air-dielectric', 'entry-line'],
    notes:
      'Solid silver conductors suspended in air-filled PTFE. Silver is about 8 percent less resistive than copper, which at 24 AWG means roughly 0.155 ohm per metre loop instead of 0.168 -- a difference with no consequence whatever into a line input. The air spacing is the part that does something computable, holding capacitance near 28 pF per metre.',
    provenanceNote:
      'Conductor purity, air-spaced PTFE and shield arrangement are as Argento describes them. Gauge is inferred from the stated conductor diameter; Argento publishes no resistance, inductance or capacitance, so all three are derived.',
  }),

  makeCable({
    mfr: 'Argento Audio',
    model: 'Organic Speaker Cable',
    cableType: 'speaker',
    price: usd(4200, 'APPROXIMATE', 2024, 'Approximate cost of a 2.5 m terminated pair.'),
    priceLengthM: 2.5,
    country: 'Denmark',
    conductor: 'pure-silver',
    geometry: 'solid-core',
    gauge: 14,
    dielectric: 'PTFE',
    termination: ['spade', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#aab4c0',
    tags: ['silver-conductor', 'entry-line'],
    notes:
      'Solid silver conductors at about 14 AWG equivalent, which works out near 0.015 ohm per metre loop. A widely spaced solid-core pair has low capacitance and correspondingly high inductance, around 0.75 uH per metre; over a 2.5 m run that is roughly 0.24 ohm of reactance at 20 kHz, which the engine puts into the divider against the loudspeaker impedance at that frequency.',
    provenanceNote:
      'Conductor material and construction from Argento published descriptions. Gauge inferred from the conductor count and diameter; electrical values derived.',
  }),

  makeCable({
    mfr: 'Argento Audio',
    model: 'Organic Power Cord',
    cableType: 'power',
    price: usd(3200, 'APPROXIMATE', 2024, 'Approximate cost of a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'Denmark',
    conductor: 'pure-silver',
    geometry: 'twisted-pair',
    gauge: 13,
    currentRatingA: 16,
    shielding: 'Shielded',
    termination: ['IEC C13', 'IEC C15', 'Schuko'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#aab4c0',
    tags: ['silver-conductor', 'shielded'],
    notes:
      'A shielded mains cord with solid silver live and neutral conductors at roughly 13 AWG, rated 16 A, terminated in Schuko and IEC for European supplies. Silver at mains gauges is an expensive way to buy a small reduction in an already tiny series resistance, and that is the whole of the description here: the engine applies no signal processing to power cords.',
    provenanceNote:
      'Conductor material, shielding and connector options from Argento published descriptions. Gauge inferred. No electrical model is applied to power cords; the derived figures are recorded for completeness only.',
  }),

  makeCable({
    mfr: 'Argento Audio',
    model: 'Organic Reference Interconnect',
    cableType: 'interconnect',
    price: usd(4200, 'APPROXIMATE', 2024, 'Approximate cost of a 1 m pair.'),
    priceLengthM: 1,
    country: 'Denmark',
    conductor: 'pure-silver',
    geometry: 'air-tube',
    gauge: 23,
    shielding: 'Braided shield, floated at one end',
    dielectric: 'Air within PTFE tubes',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#9aa5b3',
    tags: ['silver-conductor', 'air-dielectric', 'mid-line'],
    notes:
      'The Reference version raises the silver conductor section and the tube diameter. Widening the spacing lowers capacitance a little further and raises inductance in step -- the trade is unavoidable -- and at interconnect scale only the capacitance side of it has any bearing on the result.',
    provenanceNote:
      'Construction from Argento published descriptions. Gauge inferred from the stated conductor diameter; electrical values derived.',
  }),

  makeCable({
    mfr: 'Argento Audio',
    model: 'Organic Reference Speaker Cable',
    cableType: 'speaker',
    price: usd(7800, 'APPROXIMATE', 2024, 'Approximate cost of a 2.5 m terminated pair.'),
    priceLengthM: 2.5,
    country: 'Denmark',
    conductor: 'pure-silver',
    geometry: 'air-tube',
    gauge: 14,
    dielectric: 'Air within PTFE tubes',
    termination: ['spade', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#9aa5b3',
    tags: ['silver-conductor', 'air-dielectric', 'mid-line'],
    notes:
      'Silver conductors moved into air tubes for the Reference speaker cable. Capacitance drops to about 28 pF per metre, which no loudspeaker cares about, and inductance rises to about 0.9 uH per metre, which on a long run into a 3 ohm minimum is the term that actually costs a tenth of a decibel at the top of the band. The engine works out the number for the run length selected.',
    provenanceNote:
      'Construction from Argento published descriptions. Gauge inferred from conductor count and diameter; electrical values derived.',
  }),

  makeCable({
    mfr: 'Argento Audio',
    model: 'Flow Master Reference Interconnect',
    cableType: 'interconnect',
    price: usd(9500, 'APPROXIMATE', 2024, 'Approximate cost of a 1 m pair.'),
    priceLengthM: 1,
    country: 'Denmark',
    conductor: 'pure-silver',
    geometry: 'air-tube',
    gauge: 22,
    shielding: 'Braided shield, floated at one end',
    dielectric: 'Air within PTFE tubes',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#8b96a4',
    tags: ['silver-conductor', 'air-dielectric', 'upper-line'],
    notes:
      'Heavier solid silver in larger air tubes, with a shield grounded at a single end so no return current flows in it. The engine reads about 28 pF per metre and a loop resistance near 0.098 ohm per metre, and reports the in-band effect of both, which for a 1 m line-level run rounds to zero.',
    provenanceNote:
      'Construction and shield arrangement from Argento published descriptions of the Flow Master Reference line. Gauge inferred; electrical values derived.',
  }),

  makeCable({
    mfr: 'Argento Audio',
    model: 'Flow Master Reference Speaker Cable',
    cableType: 'speaker',
    price: usd(16000, 'APPROXIMATE', 2024, 'Approximate cost of a 2.5 m terminated pair.'),
    priceLengthM: 2.5,
    country: 'Denmark',
    conductor: 'pure-silver',
    geometry: 'air-tube',
    gauge: 13,
    dielectric: 'Air within PTFE tubes',
    termination: ['spade', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#8b96a4',
    tags: ['silver-conductor', 'air-dielectric', 'upper-line'],
    notes:
      'More silver in the same air-tube arrangement, about 13 AWG equivalent and near 0.012 ohm per metre loop -- some 8 percent below copper at the same gauge, which is exactly the resistivity ratio and nothing more. Over a 2.5 m pair the resistance difference against a copper cable of the same section is about 0.003 ohm.',
    provenanceNote:
      'Construction from Argento published descriptions. Gauge inferred from conductor count and diameter; electrical values derived.',
  }),

  makeCable({
    mfr: 'Argento Audio',
    model: 'Flow Master Reference Power Cord',
    cableType: 'power',
    price: usd(8500, 'APPROXIMATE', 2024, 'Approximate cost of a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'Denmark',
    conductor: 'pure-silver',
    geometry: 'twisted-pair',
    gauge: 12,
    currentRatingA: 16,
    shielding: 'Shielded',
    termination: ['IEC C13', 'IEC C15', 'IEC C19', 'Schuko'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#8b96a4',
    tags: ['silver-conductor', 'shielded', 'upper-line'],
    notes:
      'Solid silver mains conductors at roughly 12 AWG, shielded, rated 16 A, with C19 available for larger amplifiers. As engineering it is a well-made, very expensive cord; as far as this engine is concerned it sits upstream of a rectifier and a regulator and receives no signal processing at all.',
    provenanceNote:
      'Conductor material, gauge, shielding and connector options from Argento published descriptions. No electrical model is applied to power cords.',
  }),

  makeCable({
    mfr: 'Argento Audio',
    model: 'Serenity Master Reference Interconnect',
    cableType: 'interconnect',
    price: usd(24000, 'APPROXIMATE', 2024, 'Approximate cost of a 1 m pair.'),
    priceLengthM: 1,
    country: 'Denmark',
    conductor: 'pure-silver',
    geometry: 'air-tube',
    gauge: 21,
    shielding: 'Braided shield, floated at one end',
    dielectric: 'Air within PTFE tubes',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#7c8794',
    tags: ['silver-conductor', 'air-dielectric', 'flagship'],
    notes:
      'The top Argento interconnect: the largest silver section the line uses, in the largest air tubes, with the same single-ended shield grounding. Its derived capacitance is essentially the same as the entry interconnect in the range at a tenth of the price, because both put silver in air at similar spacing, and the model reports that plainly rather than manufacturing a difference.',
    provenanceNote:
      'Construction from Argento published descriptions of the Serenity Master Reference line. Gauge inferred; electrical values derived.',
  }),

  makeCable({
    mfr: 'Argento Audio',
    model: 'Serenity Speaker Cable',
    cableType: 'speaker',
    price: usd(28000, 'APPROXIMATE', 2024, 'Approximate cost of a 2.5 m terminated pair.'),
    priceLengthM: 2.5,
    country: 'Denmark',
    conductor: 'pure-silver',
    geometry: 'air-tube',
    gauge: 12,
    dielectric: 'Air within PTFE tubes',
    termination: ['spade', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#7c8794',
    tags: ['silver-conductor', 'air-dielectric', 'flagship'],
    notes:
      'The heaviest silver conductor Argento offers for loudspeakers, near 12 AWG equivalent and about 0.0096 ohm per metre loop. Even here the air-spaced geometry keeps inductance around 0.9 uH per metre, so on runs beyond about 4 m the high-frequency reactance outweighs the resistance in the model. That is a property of the construction, and it is the reason the geometry field exists.',
    provenanceNote:
      'Construction from Argento published descriptions. Gauge inferred from conductor count and diameter; electrical values derived.',
  }),

  // =========================================================================
  // Skogrand Cables -- Norway. The whole range is one idea taken to an
  // extreme: solid conductors held in very large air-filled PTFE tubes, which
  // is why the cables are so bulky for their conductor section.
  // =========================================================================
  makeCable({
    mfr: 'Skogrand Cables',
    model: 'SC Beethoven Speaker Cable',
    cableType: 'speaker',
    price: usd(4500, 'APPROXIMATE', 2024, 'Approximate cost of a 2.5 m terminated pair.'),
    priceLengthM: 2.5,
    country: 'Norway',
    conductor: 'occ-copper',
    geometry: 'air-tube',
    gauge: 14,
    dielectric: 'Air within large-bore PTFE tubes',
    termination: ['spade', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#5d86b0',
    tags: ['air-dielectric', 'large-bore', 'entry-line'],
    notes:
      'The entry Skogrand, and it already shows why the cables look the way they do: the conductor is modest, around 14 AWG, and almost all of the diameter is air inside oversized PTFE tubing. That drives capacitance down near 28 pF per metre and inductance up near 0.9 uH per metre. Into a loudspeaker the low capacitance is free and the high inductance is the bill -- roughly 0.28 ohm of reactance at 20 kHz over a 2.5 m pair.',
    provenanceNote:
      'The large-bore air-tube construction and solid conductors are as Skogrand describes them. Gauge is inferred from the conductor section rather than published as AWG, and all electrical values are derived; Skogrand publishes no resistance, inductance or capacitance.',
  }),

  makeCable({
    mfr: 'Skogrand Cables',
    model: 'SC Beethoven Interconnect',
    cableType: 'interconnect',
    price: usd(2600, 'APPROXIMATE', 2024, 'Approximate cost of a 1 m pair.'),
    priceLengthM: 1,
    country: 'Norway',
    conductor: 'occ-copper',
    geometry: 'air-tube',
    gauge: 22,
    shielding: 'Braided shield outside the tubes',
    dielectric: 'Air within large-bore PTFE tubes',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#5d86b0',
    tags: ['air-dielectric', 'large-bore', 'entry-line'],
    notes:
      'At interconnect scale the air-tube approach is on the right side of its own trade: capacitance near 28 pF per metre against 70 for a normal twisted pair, and the extra inductance is irrelevant driving a high-impedance input. A 1 m run from a 600 ohm source corners around 9 MHz.',
    provenanceNote:
      'Construction from Skogrand published descriptions. Gauge inferred from conductor section; electrical values derived.',
  }),

  makeCable({
    mfr: 'Skogrand Cables',
    model: 'SC Beethoven Power Cord',
    cableType: 'power',
    price: usd(3200, 'APPROXIMATE', 2024, 'Approximate cost of a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'Norway',
    conductor: 'occ-copper',
    geometry: 'air-tube',
    gauge: 12,
    currentRatingA: 16,
    dielectric: 'Air within large-bore PTFE tubes',
    termination: ['IEC C13', 'IEC C15', 'Schuko', 'NEMA 5-15'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#5d86b0',
    tags: ['air-dielectric', 'large-bore'],
    notes:
      'Skogrand carries the same air-tube housing into the mains cord: roughly 12 AWG conductors in oversized PTFE tubing, rated 16 A, with Schuko, IEC and NEMA options. That makes for an unusually large and stiff cord for its copper content. The engine applies no signal processing to power cords.',
    provenanceNote:
      'Construction, gauge and connector options from Skogrand published descriptions. No electrical model is applied to power cords; the derived figures are recorded for completeness only.',
  }),

  makeCable({
    mfr: 'Skogrand Cables',
    model: 'SC Ignis Speaker Cable',
    cableType: 'speaker',
    price: usd(9500, 'APPROXIMATE', 2024, 'Approximate cost of a 2.5 m terminated pair.'),
    priceLengthM: 2.5,
    country: 'Norway',
    conductor: 'occ-copper',
    geometry: 'air-tube',
    gauge: 13,
    dielectric: 'Air within large-bore PTFE tubes',
    termination: ['spade', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#4d7aa8',
    tags: ['air-dielectric', 'large-bore', 'mid-line'],
    notes:
      'More conductor in the same oversized tubes, taking the effective gauge to about 13 AWG and loop resistance to roughly 0.013 ohm per metre. The geometry and therefore the inductance are essentially unchanged from Beethoven, so on the three quantities the engine models the difference between the two is the resistance and nothing else.',
    provenanceNote:
      'Construction from Skogrand published descriptions. Gauge inferred; electrical values derived.',
  }),

  makeCable({
    mfr: 'Skogrand Cables',
    model: 'SC Ignis Interconnect',
    cableType: 'interconnect',
    price: usd(5500, 'APPROXIMATE', 2024, 'Approximate cost of a 1 m pair.'),
    priceLengthM: 1,
    country: 'Norway',
    conductor: 'occ-copper',
    geometry: 'air-tube',
    gauge: 22,
    shielding: 'Braided shield outside the tubes',
    dielectric: 'Air within large-bore PTFE tubes',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#4d7aa8',
    tags: ['air-dielectric', 'large-bore', 'mid-line'],
    notes:
      'Ignis at interconnect scale: larger tubing and heavier jacketing around the same air-spaced pair. The capacitance the model reads is set by the spacing, so it stays near the Beethoven figure; the entry does not invent a difference where the construction does not produce one.',
    provenanceNote:
      'Construction from Skogrand published descriptions. Gauge inferred; electrical values derived.',
  }),

  makeCable({
    mfr: 'Skogrand Cables',
    model: 'SC Ignis Power Cord',
    cableType: 'power',
    price: usd(6500, 'APPROXIMATE', 2024, 'Approximate cost of a 1.5 m cord.'),
    priceLengthM: 1.5,
    country: 'Norway',
    conductor: 'occ-copper',
    geometry: 'air-tube',
    gauge: 11,
    currentRatingA: 16,
    dielectric: 'Air within large-bore PTFE tubes',
    termination: ['IEC C13', 'IEC C15', 'IEC C19', 'Schuko', 'NEMA 5-15'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#4d7aa8',
    tags: ['air-dielectric', 'large-bore', 'high-current'],
    notes:
      'A heavier mains cord in the same construction, about 11 AWG per conductor and rated 16 A, with a C19 option for large amplifiers. Gauge and rating are the checkable facts. No signal processing is applied to power cords.',
    provenanceNote:
      'Gauge, rating and connector options from Skogrand published descriptions. No electrical model is applied to power cords.',
  }),

  makeCable({
    mfr: 'Skogrand Cables',
    model: 'SC Centaurus Speaker Cable',
    cableType: 'speaker',
    price: usd(18000, 'APPROXIMATE', 2024, 'Approximate cost of a 2.5 m terminated pair.'),
    priceLengthM: 2.5,
    country: 'Norway',
    conductor: 'occ-copper',
    geometry: 'air-tube',
    gauge: 12,
    dielectric: 'Air within large-bore PTFE tubes',
    termination: ['spade', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#3f6b98',
    tags: ['air-dielectric', 'large-bore', 'upper-line'],
    notes:
      'About 12 AWG equivalent in the largest tubing Skogrand uses below the flagship, near 0.010 ohm per metre loop. Over a 2.5 m pair that is roughly 0.026 ohm of series resistance against about 0.28 ohm of reactance at 20 kHz -- a useful illustration that for an air-spaced cable the inductance term dominates at the top of the band.',
    provenanceNote:
      'Construction from Skogrand published descriptions. Gauge inferred; electrical values derived.',
  }),

  makeCable({
    mfr: 'Skogrand Cables',
    model: 'SC Centaurus Interconnect',
    cableType: 'interconnect',
    price: usd(11000, 'APPROXIMATE', 2024, 'Approximate cost of a 1 m pair.'),
    priceLengthM: 1,
    country: 'Norway',
    conductor: 'occ-copper',
    geometry: 'air-tube',
    gauge: 21,
    shielding: 'Braided shield outside the tubes',
    dielectric: 'Air within large-bore PTFE tubes',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#3f6b98',
    tags: ['air-dielectric', 'large-bore', 'upper-line'],
    notes:
      'Heavier conductors in the largest air tubes, shielded outside the tubing so the shield does not close the spacing that sets the capacitance. That detail is the reason the low capacitance survives the addition of a shield at all.',
    provenanceNote:
      'Construction and shield placement from Skogrand published descriptions. Gauge inferred; electrical values derived.',
  }),

  makeCable({
    mfr: 'Skogrand Cables',
    model: 'SC Markarian 421 Speaker Cable',
    cableType: 'speaker',
    price: usd(38000, 'APPROXIMATE', 2024, 'Approximate cost of a 2.5 m terminated pair.'),
    priceLengthM: 2.5,
    country: 'Norway',
    conductor: 'occ-copper',
    geometry: 'air-tube',
    gauge: 11,
    dielectric: 'Air within large-bore PTFE tubes',
    termination: ['spade', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#345a80',
    tags: ['air-dielectric', 'large-bore', 'flagship'],
    notes:
      'The flagship: the most conductor Skogrand fits into the construction, around 11 AWG equivalent and roughly 0.008 ohm per metre loop. It is the lowest resistance in the Skogrand range and it shares the range inductance, so in the model it differs from SC Centaurus by about 0.006 ohm over a 2.5 m pair.',
    provenanceNote:
      'Construction from Skogrand published descriptions of the Markarian 421 line. Gauge inferred; electrical values derived.',
  }),

  makeCable({
    mfr: 'Skogrand Cables',
    model: 'SC Markarian 421 Interconnect',
    cableType: 'interconnect',
    price: usd(22000, 'APPROXIMATE', 2024, 'Approximate cost of a 1 m pair.'),
    priceLengthM: 1,
    country: 'Norway',
    conductor: 'occ-copper',
    geometry: 'air-tube',
    gauge: 21,
    shielding: 'Braided shield outside the tubes',
    dielectric: 'Air within large-bore PTFE tubes',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#345a80',
    tags: ['air-dielectric', 'large-bore', 'flagship'],
    notes:
      'The flagship interconnect, and electrically the same proposition as the rest of the range: a shielded air-spaced pair near 28 pF per metre. The engine will compute an in-band deviation of about 0.00 dB for a 1 m run into any normal input, which is the correct result for this cable and for most of the cables around it.',
    provenanceNote:
      'Construction from Skogrand published descriptions. Gauge inferred; electrical values derived.',
  }),

  // =========================================================================
  // Ansuz Acoustics -- Denmark, part of Audio Group Denmark. The tier ladder
  // (X2, A2, D2, D-TC, D-TC Supreme) is defined by how many of the maker
  // passive resonant coil modules and how much zirconium and tungsten surface
  // treatment a given cable carries. Those are construction facts; they are
  // not circuit elements in this model and nothing here says they are.
  // =========================================================================
  makeCable({
    mfr: 'Ansuz Acoustics',
    model: 'Speakz X2',
    cableType: 'speaker',
    price: usd(3200, 'APPROXIMATE', 2024, 'Approximate cost of a 2 m terminated pair.'),
    priceLengthM: 2,
    country: 'Denmark',
    conductor: 'silver-plated-copper',
    geometry: 'braided',
    gauge: 14,
    dielectric: 'PTFE',
    termination: ['spade', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#8f74c4',
    tags: ['entry-line', 'braided'],
    notes:
      'The entry Speakz: silver-plated copper conductors in a braid, about 14 AWG equivalent and near 0.017 ohm per metre loop. The plating is a surface layer, so the direct-current path is still the copper core and the resistivity used here is the copper figure. The braid keeps inductance around 0.4 uH per metre, which is low for a speaker cable.',
    provenanceNote:
      'Conductor material, plating and braided construction are as Ansuz describes them. Gauge is inferred from cable size rather than published, and all electrical values are derived; Ansuz publishes no resistance, inductance or capacitance.',
  }),

  makeCable({
    mfr: 'Ansuz Acoustics',
    model: 'Speakz A2',
    cableType: 'speaker',
    price: usd(7000, 'APPROXIMATE', 2024, 'Approximate cost of a 2 m terminated pair.'),
    priceLengthM: 2,
    country: 'Denmark',
    conductor: 'silver-plated-copper',
    geometry: 'braided',
    gauge: 13,
    dielectric: 'PTFE',
    termination: ['spade', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#8f74c4',
    tags: ['mid-line', 'braided', 'coil-modules'],
    notes:
      'A heavier braid, about 13 AWG equivalent, with the maker passive resonant coil modules fitted along the cable. Those modules are real hardware and Ansuz describes them as noise-dissipation networks; this database does not assert what they do to the signal, and the engine reads only the derived loop resistance of roughly 0.013 ohm per metre and the braid inductance near 0.4 uH per metre.',
    provenanceNote:
      'Construction and the presence of the coil modules are as Ansuz describes them. Their electrical behaviour is not published in a form this model could use, so they contribute nothing to the derived figures. Gauge inferred.',
  }),

  makeCable({
    mfr: 'Ansuz Acoustics',
    model: 'Speakz D2',
    cableType: 'speaker',
    price: usd(15000, 'APPROXIMATE', 2024, 'Approximate cost of a 2 m terminated pair.'),
    priceLengthM: 2,
    country: 'Denmark',
    conductor: 'pure-silver',
    geometry: 'braided',
    gauge: 13,
    dielectric: 'PTFE',
    termination: ['spade', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#7f61b8',
    tags: ['silver-conductor', 'upper-line', 'braided'],
    notes:
      'The D tier moves to solid silver conductors. At the same 13 AWG equivalent that is about 0.012 ohm per metre loop against 0.013 for copper -- the 8 percent resistivity difference, which over a 2 m pair amounts to roughly 0.002 ohm. The geometry is unchanged, so the inductance is unchanged.',
    provenanceNote:
      'Conductor material and construction from Ansuz published descriptions. Gauge inferred; electrical values derived.',
  }),

  makeCable({
    mfr: 'Ansuz Acoustics',
    model: 'Speakz D-TC Supreme',
    cableType: 'speaker',
    price: usd(62000, 'APPROXIMATE', 2024, 'Approximate cost of a 2 m terminated pair; sold to order.'),
    priceLengthM: 2,
    country: 'Denmark',
    conductor: 'pure-silver',
    geometry: 'braided',
    gauge: 12,
    dielectric: 'PTFE',
    termination: ['spade', 'banana'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#6f51a8',
    tags: ['silver-conductor', 'flagship', 'zirconium-treated'],
    notes:
      'The flagship: silver conductors at about 12 AWG equivalent, zirconium and tungsten surface treatment, and the largest complement of coil modules in the range. Loop resistance works out near 0.0096 ohm per metre, the lowest of any Ansuz speaker cable, and the braid holds inductance around 0.4 uH per metre. The surface treatments and modules are recorded as construction and take no part in the calculation.',
    provenanceNote:
      'Conductor, surface treatments and module count from Ansuz published descriptions. Gauge inferred; electrical values derived, and the coil modules and coatings contribute nothing to them because no usable published data exists for either.',
  }),

  makeCable({
    mfr: 'Ansuz Acoustics',
    model: 'Signalz X2',
    cableType: 'interconnect',
    price: usd(2200, 'APPROXIMATE', 2024, 'Approximate cost of a 1 m pair.'),
    priceLengthM: 1,
    country: 'Denmark',
    conductor: 'silver-plated-copper',
    geometry: 'twisted-pair',
    gauge: 22,
    shielding: 'Braided shield',
    dielectric: 'PTFE',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#8f74c4',
    tags: ['entry-line', 'shielded'],
    notes:
      'A shielded twisted pair of silver-plated copper, offered single-ended or balanced. Around 70 pF per metre, so a 1 m run into a normal input is a low-pass corner in the megahertz. The engine computes it and reports the audio-band result, which is no measurable change.',
    provenanceNote:
      'Conductor, shielding and termination options from Ansuz published descriptions. Gauge inferred; electrical values derived.',
  }),

  makeCable({
    mfr: 'Ansuz Acoustics',
    model: 'Signalz A2',
    cableType: 'interconnect',
    price: usd(4600, 'APPROXIMATE', 2024, 'Approximate cost of a 1 m pair.'),
    priceLengthM: 1,
    country: 'Denmark',
    conductor: 'silver-plated-copper',
    geometry: 'twisted-pair',
    gauge: 22,
    shielding: 'Braided shield with coil modules fitted along the run',
    dielectric: 'PTFE',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#8f74c4',
    tags: ['mid-line', 'shielded', 'coil-modules'],
    notes:
      'The A tier adds the passive coil modules to the same shielded pair. Nothing about the conductor or the spacing changes, so the derived capacitance is the same as Signalz X2; the entry states that instead of implying the tiers must sound different because they cost different amounts.',
    provenanceNote:
      'Construction from Ansuz published descriptions. Gauge inferred; electrical values derived and, by construction, identical to Signalz X2.',
  }),

  makeCable({
    mfr: 'Ansuz Acoustics',
    model: 'Signalz D-TC',
    cableType: 'interconnect',
    price: usd(20000, 'APPROXIMATE', 2024, 'Approximate cost of a 1 m pair.'),
    priceLengthM: 1,
    country: 'Denmark',
    conductor: 'pure-silver',
    geometry: 'twisted-pair',
    gauge: 21,
    shielding: 'Braided shield with coil modules fitted along the run',
    dielectric: 'PTFE',
    termination: ['RCA', 'XLR'],
    balanced: true,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#7f61b8',
    tags: ['silver-conductor', 'flagship', 'zirconium-treated'],
    notes:
      'Solid silver conductors with the zirconium and tungsten surface treatment and the full module complement. At line level the conductor metal has no consequence the model can find -- 21 AWG silver is about 0.077 ohm per metre loop against an input impedance of tens of kilohms -- and the capacitance is set by the twisted-pair spacing at roughly 70 pF per metre.',
    provenanceNote:
      'Conductor, surface treatment and module count from Ansuz published descriptions. Gauge inferred; electrical values derived.',
  }),

  makeCable({
    mfr: 'Ansuz Acoustics',
    model: 'Mainz X2',
    cableType: 'power',
    price: usd(2400, 'APPROXIMATE', 2024, 'Approximate cost of a 2 m cord.'),
    priceLengthM: 2,
    country: 'Denmark',
    conductor: 'silver-plated-copper',
    geometry: 'twisted-pair',
    gauge: 13,
    currentRatingA: 16,
    shielding: 'Shielded',
    termination: ['IEC C13', 'IEC C15', 'Schuko'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#8f74c4',
    tags: ['entry-line', 'shielded'],
    notes:
      'The entry Mainz cord: shielded silver-plated copper at roughly 13 AWG per core, rated 16 A, with Schuko and IEC ends for European supplies. The engine applies no signal processing to power cords, so this entry records the build and the price.',
    provenanceNote:
      'Conductor, shielding, rating and connector options from Ansuz published descriptions. Gauge inferred. No electrical model is applied to power cords; the derived figures are recorded for completeness only.',
  }),

  makeCable({
    mfr: 'Ansuz Acoustics',
    model: 'Mainz D2',
    cableType: 'power',
    price: usd(7800, 'APPROXIMATE', 2024, 'Approximate cost of a 2 m cord.'),
    priceLengthM: 2,
    country: 'Denmark',
    conductor: 'pure-silver',
    geometry: 'twisted-pair',
    gauge: 12,
    currentRatingA: 16,
    shielding: 'Shielded, with coil modules fitted along the run',
    termination: ['IEC C13', 'IEC C15', 'IEC C19', 'Schuko'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#7f61b8',
    tags: ['silver-conductor', 'shielded', 'coil-modules'],
    notes:
      'Solid silver mains conductors at about 12 AWG, shielded, rated 16 A, with the coil modules fitted and a C19 option for larger amplifiers. Those are the facts of the build. No signal processing is applied to power cords in this engine, and this entry makes no claim beyond the construction.',
    provenanceNote:
      'Conductor, gauge, shielding, module fitment and connector options from Ansuz published descriptions. No electrical model is applied to power cords.',
  }),

  makeCable({
    mfr: 'Ansuz Acoustics',
    model: 'Mainz D-TC Supreme',
    cableType: 'power',
    price: usd(28000, 'APPROXIMATE', 2024, 'Approximate cost of a 2 m cord; sold to order.'),
    priceLengthM: 2,
    country: 'Denmark',
    conductor: 'pure-silver',
    geometry: 'twisted-pair',
    gauge: 11,
    currentRatingA: 16,
    shielding: 'Shielded, with the full complement of coil modules',
    termination: ['IEC C15', 'IEC C19', 'Schuko'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#6f51a8',
    tags: ['silver-conductor', 'flagship', 'zirconium-treated', 'high-current'],
    notes:
      'The flagship mains cord: silver conductors near 11 AWG, zirconium and tungsten surface treatment, the largest module count in the range, and C19 termination for high-current loads. It is a substantial and carefully made piece of hardware and the engine applies exactly zero digital signal processing to it, which is the honest position for a mains cord and is stated rather than hidden.',
    provenanceNote:
      'Conductor, gauge, surface treatment, module count and connector options from Ansuz published descriptions. No electrical model is applied to power cords.',
  }),

  makeCable({
    mfr: 'Ansuz Acoustics',
    model: 'Digitalz A2 USB',
    cableType: 'usb',
    price: usd(3500, 'APPROXIMATE', 2024, 'Approximate cost of a 1.5 m A-to-B assembly.'),
    priceLengthM: 1.5,
    country: 'Denmark',
    conductor: 'silver-plated-copper',
    geometry: 'twisted-pair',
    gauge: 26,
    characteristicOhms: 90,
    digitalStandard: 'USB 2.0 High Speed, 480 Mbit/s',
    shielding: 'Foil over the data pair plus an overall braid, with coil modules fitted',
    termination: ['USB A to B'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#8f74c4',
    tags: ['usb-2.0', 'coil-modules'],
    notes:
      'A 90 ohm USB 2.0 assembly with a foiled data pair, an overall braid and the maker coil modules fitted to the run, terminated A to B. USB audio is packetised and error-checked and an asynchronous DAC generates its own clock, so a compliant cable carries the packets or the link fails. The engine applies no signal processing to USB cables.',
    provenanceNote:
      'Construction, module fitment and standard from Ansuz published descriptions. Gauge is the usual figure for a USB 2.0 data pair. No electrical model is applied to USB cables.',
  }),

  makeCable({
    mfr: 'Ansuz Acoustics',
    model: 'Digitalz D2 Ethernet',
    cableType: 'ethernet',
    price: usd(6500, 'APPROXIMATE', 2024, 'Approximate cost of a 1.5 m patch lead.'),
    priceLengthM: 1.5,
    country: 'Denmark',
    conductor: 'pure-silver',
    geometry: 'twisted-pair',
    gauge: 24,
    characteristicOhms: 100,
    digitalStandard: 'Gigabit Ethernet over RJ45, 1000BASE-T',
    shielding: 'Shielded pairs within an overall braid, with coil modules fitted',
    termination: ['RJ45'],
    confidence: 'MANUFACTURER_SPEC',
    accent: '#7f61b8',
    tags: ['rj45', 'shielded', 'coil-modules'],
    notes:
      'A shielded 100 ohm RJ45 lead with silver conductors and the coil modules fitted. Ansuz describes it by construction rather than by a TIA category, so no Cat rating is claimed for it here. Ethernet is packetised, checksummed and retransmitted on error, and a streamer buffers seconds of playback ahead of the DAC, so there is no analog path between the two ends. The engine applies no signal processing to Ethernet cables.',
    provenanceNote:
      'Conductor, shielding and module fitment from Ansuz published descriptions. No Cat category is published for this cable, so the digital standard field records only the link type it is sold for; gauge is the usual figure for a shielded twisted-pair patch lead. No electrical model is applied to Ethernet cables.',
  }),
];
